import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Users, Mail, Disc3, BookOpen, BarChart3, HandCoins, Settings, LayoutDashboard,
  Plus, Edit3, Trash2, X, Check,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { http, clearToken, getToken } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Artwork from "@/components/Artwork";
import { TID } from "@/constants/testIds";

const COLORS = ["#FF9933", "#FFD700", "#e5e5e5", "#a0a0a0", "#5b5b5b"];

const Stat = ({ Icon, label, value, hint }) => (
  <div className="glass rounded-2xl p-5 flex items-center gap-4">
    <div className="w-11 h-11 rounded-full bg-[#FF9933]/10 border border-[#FF9933]/30 flex items-center justify-center text-[#FF9933]">
      <Icon size={18} />
    </div>
    <div className="min-w-0">
      <div className="text-3xl font-display text-white leading-none">{value ?? "—"}</div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mt-1 truncate">{label}</div>
      {hint && <div className="text-[10px] text-white/40 mt-0.5">{hint}</div>}
    </div>
  </div>
);

const AnalyticsPanel = () => {
  const [overview, setOverview] = useState(null);
  const [range, setRange] = useState("week");
  const [series, setSeries] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [countries, setCountries] = useState([]);
  const [devices, setDevices] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [searches, setSearches] = useState([]);
  const [referrers, setReferrers] = useState([]);

  const load = () => {
    http.get("/admin/analytics/overview").then((r) => setOverview(r.data.totals));
    http.get("/admin/analytics/timeseries", { params: { range } }).then((r) => setSeries(r.data.series));
    http.get("/admin/analytics/tracks").then((r) => setTracks(r.data));
    http.get("/admin/analytics/countries").then((r) => setCountries(r.data));
    http.get("/admin/analytics/devices").then((r) => setDevices(r.data));
    http.get("/admin/analytics/platforms").then((r) => setPlatforms(r.data));
    http.get("/admin/analytics/searches").then((r) => setSearches(r.data));
    http.get("/admin/analytics/referrers").then((r) => setReferrers(r.data));
  };
  useEffect(load, [range]);

  const t = overview || {};
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat Icon={Users} label="Homepage Visitors" value={t.pageviews_30d} hint="Last 30 days" />
        <Stat Icon={Users} label="Returning Visitors" value={t.returning_visitors} />
        <Stat Icon={BarChart3} label="Plays" value={t.plays} />
        <Stat Icon={HandCoins} label="Donations" value={`₹${(t.donations_total_inr || 0).toLocaleString("en-IN")}`} hint={`${t.donations_count || 0} contributions`} />
        <Stat Icon={Disc3} label="Spotify Clicks" value={t.spotify_clicks} />
        <Stat Icon={Disc3} label="YouTube Clicks" value={t.youtube_clicks} />
        <Stat Icon={Disc3} label="Apple Music Clicks" value={t.apple_clicks} />
        <Stat Icon={Mail} label="Newsletter Subscribers" value={t.subscribers} />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9933]">Time Series</div>
        <div className="flex items-center gap-1 glass rounded-full p-1">
          {["day", "week", "month"].map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              data-testid={`range-${r}`}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] rounded-full transition ${range === r ? "bg-[#FF9933] text-black" : "text-white/60 hover:text-white"}`}
            >
              {r === "day" ? "24h" : r === "week" ? "7d" : "30d"}
            </button>
          ))}
        </div>
      </div>

      <div className="glass rounded-2xl p-4">
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="day" tick={{ fill: "#a0a0a0", fontSize: 11 }} />
              <YAxis tick={{ fill: "#a0a0a0", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #FF9933", borderRadius: 8 }} labelStyle={{ color: "#FF9933" }} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#a0a0a0" }} />
              <Line type="monotone" dataKey="pageview" stroke="#FF9933" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="play" stroke="#FFD700" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="platform_click" stroke="#e5e5e5" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="glass rounded-2xl p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9933]">Most Streamed Songs</div>
          <div className="mt-4 space-y-3">
            {tracks.length === 0 && <div className="text-white/50 text-sm">No plays tracked yet.</div>}
            {tracks.map((t, i) => (
              <div key={t.release.id} className="flex items-center gap-3">
                <div className="w-6 text-[11px] text-white/40 text-right">{i + 1}</div>
                <div className="w-10 h-10"><Artwork src={t.release.cover_image} alt="" className="w-full h-full object-cover" rounded="rounded" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{t.release.title}</div>
                  <div className="text-[11px] text-white/50 truncate">{t.release.genre}</div>
                </div>
                <div className="text-sm text-[#FF9933]">{t.plays}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9933]">Platform Clicks</div>
          <div style={{ width: "100%", height: 240 }} className="mt-2">
            <ResponsiveContainer>
              <BarChart data={platforms}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="platform" tick={{ fill: "#a0a0a0", fontSize: 11 }} />
                <YAxis tick={{ fill: "#a0a0a0", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #FF9933", borderRadius: 8 }} />
                <Bar dataKey="count" fill="#FF9933" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9933]">Devices</div>
          <div style={{ width: "100%", height: 240 }} className="mt-2">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={devices} dataKey="count" nameKey="device" outerRadius={80} label={{ fill: "#e5e5e5", fontSize: 11 }}>
                  {devices.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #FF9933", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9933]">Top Countries</div>
          <div className="mt-4 space-y-2">
            {countries.length === 0 && <div className="text-white/50 text-sm">No country data yet.</div>}
            {countries.map((c) => (
              <div key={c.country} className="flex items-center justify-between text-sm">
                <span className="text-white">{c.country || "—"}</span>
                <span className="text-[#FF9933]">{c.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9933]">Top Searches</div>
          <div className="mt-4 space-y-2">
            {searches.length === 0 && <div className="text-white/50 text-sm">No searches yet.</div>}
            {searches.map((s) => (
              <div key={s.query} className="flex items-center justify-between text-sm">
                <span className="text-white truncate">{s.query}</span>
                <span className="text-[#FF9933]">{s.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9933]">Top Referrers</div>
          <div className="mt-4 space-y-2">
            {referrers.length === 0 && <div className="text-white/50 text-sm">No referrers yet.</div>}
            {referrers.map((r) => (
              <div key={r.referrer} className="flex items-center justify-between text-sm">
                <span className="text-white truncate max-w-[60%]" title={r.referrer}>{r.referrer.replace(/^https?:\/\//, "").slice(0, 40)}</span>
                <span className="text-[#FF9933]">{r.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ReleaseForm = ({ initial, onDone }) => {
  const [f, setF] = useState(initial || {
    title: "", hindi_title: "", genre: "", year: 2025, duration: "", cover_image: "",
    spotify_url: "", apple_music_url: "", youtube_url: "", spotify_embed_url: "",
    description: "", categories: [], is_featured: false, is_trending: false, credits: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, year: Number(f.year), categories: typeof f.categories === "string" ? f.categories.split(",").map(s => s.trim()).filter(Boolean) : f.categories };
      if (initial?.id) await http.put(`/admin/releases/${initial.id}`, payload);
      else await http.post("/admin/releases", payload);
      toast.success("Saved");
      onDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const field = (k, label, type = "text") => (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">{label}</span>
      <input value={f[k] || ""} onChange={(e) => set(k, e.target.value)} type={type}
             className="mt-2 w-full bg-black/60 border border-white/10 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-[#FF9933]" />
    </label>
  );

  return (
    <div className="glass rounded-2xl p-6 space-y-4">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9933]">{initial ? "Edit Release" : "New Release"}</div>
      <div className="grid md:grid-cols-2 gap-4">
        {field("title", "Title")}
        {field("hindi_title", "Hindi Title")}
        {field("genre", "Genre")}
        {field("year", "Year", "number")}
        {field("duration", "Duration (e.g. 7:20)")}
        {field("cover_image", "Cover Image URL")}
        {field("spotify_url", "Spotify URL")}
        {field("spotify_embed_url", "Spotify Embed URL")}
        {field("apple_music_url", "Apple Music URL")}
        {field("youtube_url", "YouTube URL")}
        {field("credits", "Credits")}
        <label className="block md:col-span-2">
          <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">Categories (comma separated)</span>
          <input
            value={Array.isArray(f.categories) ? f.categories.join(", ") : f.categories}
            onChange={(e) => set("categories", e.target.value)}
            className="mt-2 w-full bg-black/60 border border-white/10 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-[#FF9933]"
            placeholder="hanuman, workout, trending"
          />
        </label>
        <label className="block md:col-span-2">
          <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">Description</span>
          <textarea value={f.description || ""} onChange={(e) => set("description", e.target.value)} rows={3}
                    className="mt-2 w-full bg-black/60 border border-white/10 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-[#FF9933] resize-none" />
        </label>
      </div>
      <div className="flex items-center gap-6 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.is_featured} onChange={(e) => set("is_featured", e.target.checked)} /> Featured</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.is_trending} onChange={(e) => set("is_trending", e.target.checked)} /> Trending</label>
      </div>
      <div className="flex items-center gap-3">
        <button disabled={saving} onClick={save} className="inline-flex items-center gap-2 rounded-full bg-[#FF9933] text-black px-5 py-2 text-[10px] uppercase tracking-[0.3em] font-semibold hover:bg-[#FFD700] disabled:opacity-60">
          <Check size={12} /> Save
        </button>
        <button onClick={() => onDone(false)} className="inline-flex items-center gap-2 rounded-full border border-white/10 text-white/70 px-5 py-2 text-[10px] uppercase tracking-[0.3em] hover:text-white">
          <X size={12} /> Cancel
        </button>
      </div>
    </div>
  );
};

const SongsPanel = () => {
  const [releases, setReleases] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = () => http.get("/releases").then((r) => setReleases(r.data));
  useEffect(load, []);

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    await http.delete(`/admin/releases/${r.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9933]">Songs · {releases.length}</div>
        <button onClick={() => { setCreating(true); setEditing(null); }} data-testid="admin-new-release" className="inline-flex items-center gap-2 rounded-full bg-[#FF9933] text-black px-4 py-2 text-[10px] uppercase tracking-[0.3em] font-semibold hover:bg-[#FFD700]">
          <Plus size={12} /> New Release
        </button>
      </div>
      {(creating || editing) && <ReleaseForm initial={editing} onDone={(ok) => { setEditing(null); setCreating(false); if (ok) load(); }} />}
      <div className="glass rounded-2xl overflow-hidden">
        {releases.map((r) => (
          <div key={r.id} className="flex items-center gap-4 px-4 py-3 border-b border-white/5 last:border-none">
            <div className="w-12 h-12"><Artwork src={r.cover_image} alt="" className="w-full h-full object-cover" rounded="rounded-md" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm truncate">{r.title}</div>
              <div className="text-[11px] text-white/50 truncate">{r.genre} · {r.year}</div>
            </div>
            <div className="hidden md:flex items-center gap-1 text-[10px] text-white/40">{(r.categories || []).slice(0, 4).map((c) => <span key={c} className="rounded-full border border-white/10 px-2 py-0.5">{c}</span>)}</div>
            <button onClick={() => setEditing(r)} className="p-2 text-white/60 hover:text-[#FF9933]"><Edit3 size={14} /></button>
            <button onClick={() => del(r)} className="p-2 text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const DonationsPanel = () => {
  const [donations, setDonations] = useState([]);
  useEffect(() => { http.get("/admin/donations").then((r) => setDonations(r.data)); }, []);
  const total = donations.filter((d) => d.status === "paid").reduce((a, b) => a + (b.amount || 0), 0);
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat Icon={HandCoins} label="Total Raised" value={`₹${total.toLocaleString("en-IN")}`} />
        <Stat Icon={HandCoins} label="Paid Contributions" value={donations.filter((d) => d.status === "paid").length} />
        <Stat Icon={HandCoins} label="Pending" value={donations.filter((d) => d.status === "pending").length} />
      </div>
      <div className="glass rounded-2xl overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-4 text-[10px] uppercase tracking-[0.3em] text-white/50 border-b border-white/5">
          <div className="col-span-2">Amount</div><div className="col-span-2">Method</div><div className="col-span-3">Supporter</div><div className="col-span-3">Message</div><div className="col-span-1">Status</div><div className="col-span-1 text-right">When</div>
        </div>
        {donations.length === 0 && <div className="px-6 py-10 text-center text-white/50 text-sm">No donations yet.</div>}
        {donations.map((d) => (
          <div key={d.id} className="grid grid-cols-12 gap-4 px-6 py-4 text-sm border-b border-white/5 last:border-none">
            <div className="col-span-2 text-white font-medium">₹{d.amount}</div>
            <div className="col-span-2 text-white/70">{d.method}</div>
            <div className="col-span-3 text-white/70 truncate">{d.supporter_name || "Anonymous"}</div>
            <div className="col-span-3 text-white/60 truncate">{d.supporter_message || "—"}</div>
            <div className="col-span-1">
              <span className={`text-[10px] uppercase tracking-[0.25em] px-2 py-1 rounded ${d.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : "bg-white/5 text-white/50"}`}>{d.status}</span>
            </div>
            <div className="col-span-1 text-white/50 text-right text-[11px]">{new Date(d.created_at).toLocaleDateString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SubsPanel = ({ subs }) => (
  <div className="glass rounded-2xl overflow-hidden">
    <div className="grid grid-cols-12 gap-4 px-6 py-4 text-[10px] uppercase tracking-[0.3em] text-white/50 border-b border-white/5">
      <div className="col-span-5">Email</div><div className="col-span-4">Name</div><div className="col-span-3">Joined</div>
    </div>
    {subs.length === 0 && <div className="px-6 py-10 text-center text-white/50 text-sm">No subscribers yet.</div>}
    {subs.map((s) => (
      <div key={s.id} className="grid grid-cols-12 gap-4 px-6 py-4 text-sm border-b border-white/5 last:border-none">
        <div className="col-span-5 text-white break-all">{s.email}</div>
        <div className="col-span-4 text-white/70">{s.name || "—"}</div>
        <div className="col-span-3 text-white/50">{new Date(s.created_at).toLocaleDateString()}</div>
      </div>
    ))}
  </div>
);

const ContactsPanel = ({ contacts }) => (
  <div className="grid gap-4">
    {contacts.length === 0 && <div className="glass rounded-2xl px-6 py-10 text-center text-white/50 text-sm">No messages yet.</div>}
    {contacts.map((c) => (
      <div key={c.id} className="glass rounded-2xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF9933] bg-[#FF9933]/10 border border-[#FF9933]/30 rounded-full px-3 py-1">{c.kind}</span>
            <div className="text-white font-medium">{c.subject}</div>
          </div>
          <div className="text-[11px] text-white/50">{new Date(c.created_at).toLocaleString()}</div>
        </div>
        <div className="mt-2 text-sm text-white/70">{c.name} · <a href={`mailto:${c.email}`} className="text-[#FF9933] hover:underline">{c.email}</a></div>
        <p className="mt-4 text-sm text-white/80 whitespace-pre-wrap">{c.message}</p>
      </div>
    ))}
  </div>
);

const AdminDashboard = () => {
  const nav = useNavigate();
  const [summary, setSummary] = useState({});
  const [subs, setSubs] = useState([]);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    if (!getToken()) { nav("/admin"); return; }
    Promise.all([
      http.get("/admin/summary"),
      http.get("/admin/subscribers"),
      http.get("/admin/contacts"),
    ]).then(([s, u, c]) => {
      setSummary(s.data); setSubs(u.data); setContacts(c.data);
    }).catch((e) => {
      if (e?.response?.status === 401) { clearToken(); nav("/admin"); }
    });
  }, [nav]);

  const logout = () => { clearToken(); nav("/admin"); };

  return (
    <section className="pt-32 pb-24 min-h-screen">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#FF9933]">Admin Console · Role: ADMIN</div>
            <h1 className="mt-2 font-display text-4xl md:text-5xl text-white tracking-tight">KASHiX Sanctum</h1>
          </div>
          <button data-testid={TID.adminLogout} onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-white/15 text-white/80 hover:text-[#FF9933] hover:border-[#FF9933]/60 px-5 py-2 text-xs uppercase tracking-[0.3em] transition-all">
            <LogOut size={14} /> Logout
          </button>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat Icon={Disc3} label="Releases" value={summary.releases} />
          <Stat Icon={BookOpen} label="Lyrics" value={summary.lyrics} />
          <Stat Icon={Users} label="Subscribers" value={summary.subscribers} />
          <Stat Icon={Mail} label={`New Contacts (${summary.new_contacts ?? 0})`} value={summary.contacts} />
        </div>

        <div className="mt-12">
          <Tabs defaultValue="analytics">
            <TabsList className="bg-transparent border border-white/10 rounded-full p-1 gap-1 flex w-fit flex-wrap">
              <TabsTrigger data-testid="admin-tab-analytics" value="analytics" className="rounded-full px-4 text-[11px] uppercase tracking-[0.2em] data-[state=active]:bg-[#FF9933] data-[state=active]:text-black"><BarChart3 size={12} className="mr-1.5 inline" /> Analytics</TabsTrigger>
              <TabsTrigger data-testid="admin-tab-songs" value="songs" className="rounded-full px-4 text-[11px] uppercase tracking-[0.2em] data-[state=active]:bg-[#FF9933] data-[state=active]:text-black"><Disc3 size={12} className="mr-1.5 inline" /> Songs</TabsTrigger>
              <TabsTrigger data-testid="admin-tab-donations" value="donations" className="rounded-full px-4 text-[11px] uppercase tracking-[0.2em] data-[state=active]:bg-[#FF9933] data-[state=active]:text-black"><HandCoins size={12} className="mr-1.5 inline" /> Donations</TabsTrigger>
              <TabsTrigger data-testid={TID.adminTabSubscribers} value="subs" className="rounded-full px-4 text-[11px] uppercase tracking-[0.2em] data-[state=active]:bg-[#FF9933] data-[state=active]:text-black"><Users size={12} className="mr-1.5 inline" /> Subscribers</TabsTrigger>
              <TabsTrigger data-testid={TID.adminTabContacts} value="contacts" className="rounded-full px-4 text-[11px] uppercase tracking-[0.2em] data-[state=active]:bg-[#FF9933] data-[state=active]:text-black"><Mail size={12} className="mr-1.5 inline" /> Contacts</TabsTrigger>
              <TabsTrigger data-testid="admin-tab-settings" value="settings" className="rounded-full px-4 text-[11px] uppercase tracking-[0.2em] data-[state=active]:bg-[#FF9933] data-[state=active]:text-black"><Settings size={12} className="mr-1.5 inline" /> Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="analytics" className="mt-8"><AnalyticsPanel /></TabsContent>
            <TabsContent value="songs" className="mt-8"><SongsPanel /></TabsContent>
            <TabsContent value="donations" className="mt-8"><DonationsPanel /></TabsContent>
            <TabsContent value="subs" className="mt-8"><SubsPanel subs={subs} /></TabsContent>
            <TabsContent value="contacts" className="mt-8"><ContactsPanel contacts={contacts} /></TabsContent>
            <TabsContent value="settings" className="mt-8">
              <div className="glass rounded-2xl p-6 space-y-4 text-white/70 text-sm">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9933]">Site Configuration</div>
                <div>Site metadata, streaming URLs, and admin credentials are managed via <code className="text-[#FF9933]">backend/.env</code>. Enable Razorpay / PayPal / GA4 by setting the corresponding env vars and restarting.</div>
                <ul className="list-disc pl-5 space-y-1 text-[11px] text-white/50">
                  <li><code>RAZORPAY_KEY_ID</code> · <code>RAZORPAY_KEY_SECRET</code></li>
                  <li><code>PAYPAL_CLIENT_ID</code> · <code>PAYPAL_CLIENT_SECRET</code> · <code>PAYPAL_ENV</code></li>
                  <li>Frontend: <code>REACT_APP_GA4_ID</code>, <code>REACT_APP_GADS_ID</code>, <code>REACT_APP_RAZORPAY_KEY_ID</code>, <code>REACT_APP_PAYPAL_CLIENT_ID</code></li>
                </ul>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </section>
  );
};

export default AdminDashboard;
